import React, { useState, useEffect } from "react";
import * as assetService from "../services/assetService";
import "../styles/BulkCreateModal.css";

const BulkCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState("");
  const [properties, setProperties] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadAssetTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedAssetType) {
      loadProperties(selectedAssetType);
    } else {
      setProperties([]);
    }
  }, [selectedAssetType]);

  const loadAssetTypes = async () => {
    try {
      const response = await assetService.getAllAssetTypes();
      // Handle both array and object with data property
      const data = Array.isArray(response) ? response : response.data || [];
      setAssetTypes(data);
    } catch (err) {
      console.error("Failed to load asset types", err);
    }
  };

  const loadProperties = async (assetTypeId) => {
    try {
      const response = await assetService.getPropertiesByAssetType(assetTypeId);
      // Handle both array and object with data property
      const data = Array.isArray(response) ? response : response.data || [];
      setProperties(data);
    } catch (err) {
      console.error("Failed to load properties", err);
    }
  };

  const generateSampleCSV = () => {
    if (!selectedAssetType) {
      setError("Please select an asset type first");
      return;
    }

    // Base columns for all assets
    const baseColumns = ["name", "asset_code", "branch_code", "status", "location", "assignedTo", "description", "purchaseDate", "warrantyExpiration", "cost"];

    // Add custom property columns
    const propertyColumns = properties.map((prop) => prop.propertyName);
    const allColumns = [...baseColumns, ...propertyColumns];

    // Create sample data row
    const sampleRow = [
      "Sample Asset Name",
      "SAMPLE1-23",
      "BR001",
      "available",
      "Office Building A",
      "John Doe",
      "Sample description",
      "2026-01-01",
      "2027-01-01",
      "1000.00",
      ...propertyColumns.map(() => ""),
    ];

    const csvContent = [allColumns.join(","), sampleRow.join(",")].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `bulk_assets_template_${selectedAssetType}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setError(null);
    } else {
      setError("Please select a valid CSV file");
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedAssetType) {
      setError("Please select an asset type");
      return;
    }
    if (!file) {
      setError("Please select a CSV file to upload");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("assetTypeId", selectedAssetType);

      await assetService.bulkCreateAssets(formData);
      alert("Assets created successfully!");
      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error("Bulk upload error:", err);

      const messages = err.response?.data?.messages;

      if (Array.isArray(messages)) {
        setError(messages.join("\n"));
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || "Failed to upload assets");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedAssetType("");
    setProperties([]);
    setFile(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const selectedTypeName = assetTypes.find((t) => t.id === selectedAssetType || t.id === Number(selectedAssetType))?.name;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Bulk Create Assets</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="assetType">Select Asset Type *</label>
            <select id="assetType" value={selectedAssetType} onChange={(e) => setSelectedAssetType(e.target.value)}>
              <option value="">-- Select Asset Type --</option>
              {assetTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {selectedAssetType && (
            <>
              <div className="template-section">
                <h3>Step 1: Download Template</h3>
                <p>
                  Download a sample CSV template for <strong>{selectedTypeName}</strong> assets.
                </p>
                <p className="info-text">Include a valid branch_code for each asset. Upload will fail if a branch code is not found.</p>
                {properties.length > 0 && (
                  <p className="info-text">
                    This template includes {properties.length} custom {properties.length === 1 ? "property" : "properties"}: {properties.map((p) => p.propertyName).join(", ")}
                  </p>
                )}
                <button className="btn btn-secondary" onClick={generateSampleCSV}>
                  Download Sample CSV
                </button>
              </div>

              <div className="upload-section">
                <h3>Step 2: Upload CSV</h3>
                <p>Fill out the template and upload your CSV file.</p>
                <div className="file-input-wrapper">
                  <input type="file" accept=".csv" onChange={handleFileChange} id="csvFile" />
                  <label htmlFor="csvFile" className="file-label">
                    {file ? file.name : "Choose CSV file..."}
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={!selectedAssetType || !file || loading}>
            {loading ? "Uploading..." : "Upload & Create Assets"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkCreateModal;
